import { BrandMark, Button, Cluster, Container, Nav } from "@humaniti/ui";
import { Link, useLocation } from "react-router-dom";

/**
 * Marketing top nav — spec §6.1 (sticky, light, blur backdrop).
 *
 * Desktop (≥ 880px):
 *   [ⓗ humaniti.]  01/Humans  02/Builders  03/Pricing  04/How it works
 *                  05/Roadmap                              [Hire Humans]
 *
 * Mobile (< 880px): brand left, hamburger right. Tap hamburger → stateless
 * `<details>`-driven drawer drops below with the same nav links + Hire
 * Humans CTA stacked vertically. Breakpoint live in marketing.css.
 *
 * Public sign-in is intentionally NOT linked from this nav. Until M1.6
 * launch the requester + annotator SPAs are not feature-complete enough to
 * advertise — the "Hire Humans" CTA routes to /coming-soon instead. Staff
 * who need to test the actual sign-in flows reach them via the admin SPA's
 * "Sign-in flows" page.
 */
const NAV_ITEMS = [
  { num: "01", label: "Humans", href: "/for-humans" },
  { num: "02", label: "Builders", href: "/for-builders" },
  { num: "03", label: "Pricing", href: "/pricing" },
  { num: "04", label: "How it works", href: "/how-it-works" },
  { num: "05", label: "Roadmap", href: "/roadmap" },
];

export const TopNav = () => {
  const { pathname } = useLocation();
  const navItems = NAV_ITEMS.map((item) => ({
    ...item,
    active: pathname === item.href,
  }));

  return (
    <header
      style={{
        borderBottom: "var(--border-1)",
        background: "rgba(250,250,247,0.85)",
        backdropFilter: "blur(12px)",
        position: "sticky",
        top: 0,
        zIndex: 10,
      }}
    >
      <Container size="full">
        <Cluster
          justify="between"
          align="center"
          gap="lg"
          style={{ paddingTop: "var(--space-3)", paddingBottom: "var(--space-3)" }}
        >
          <Link to="/" style={{ textDecoration: "none" }}>
            <BrandMark variant="full" size="lg" />
          </Link>
          {/* Desktop cluster — inline nav + Hire Humans CTA (→ coming soon) */}
          <Cluster gap="xl" align="center" className="marketing-topnav__desktop">
            <Nav items={navItems} />
            <Link to="/coming-soon?for=builders" style={{ textDecoration: "none" }}>
              <Button size="sm">Hire Humans</Button>
            </Link>
          </Cluster>
          {/* Mobile cluster — hamburger toggle that opens a drawer */}
          <div className="marketing-topnav__mobile">
            <MobileMenu navItems={navItems} />
          </div>
        </Cluster>
      </Container>
    </header>
  );
};

/**
 * Mobile menu — `<details>` drives open/close so we don't need JS state or
 * an outside-click handler. The hamburger glyph is three SVG bars; rotating
 * to an X on open is intentionally skipped at v1 (no animation framework
 * wired up; not worth the bytes).
 */
const MobileMenu = ({
  navItems,
}: {
  navItems: { num: string; label: string; href: string; active: boolean }[];
}) => (
  <details className="marketing-topnav__menu">
    <summary aria-label="Open menu">
      <HamburgerGlyph />
    </summary>
    <div className="marketing-topnav__drawer">
      <ul className="marketing-topnav__drawer-list">
        {navItems.map((item) => (
          <li key={item.href}>
            <Link to={item.href} aria-current={item.active ? "page" : undefined}>
              <span className="marketing-topnav__drawer-num">{item.num}</span>
              <span>{item.label}</span>
            </Link>
          </li>
        ))}
      </ul>
      <div className="marketing-topnav__drawer-actions">
        <Link to="/coming-soon?for=builders" style={{ textDecoration: "none" }}>
          <Button size="md" style={{ width: "100%" }}>
            Hire Humans
          </Button>
        </Link>
      </div>
    </div>
  </details>
);

const HamburgerGlyph = () => (
  <svg width="20" height="14" viewBox="0 0 20 14" fill="none" aria-hidden="true">
    <line x1="0" y1="1" x2="20" y2="1" stroke="currentColor" strokeWidth="1.5" />
    <line x1="0" y1="7" x2="20" y2="7" stroke="currentColor" strokeWidth="1.5" />
    <line x1="0" y1="13" x2="20" y2="13" stroke="currentColor" strokeWidth="1.5" />
  </svg>
);
